import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  Typography,
} from "@mui/material";
import DrawerHeader from "shared-ui/src/components/detail-drawer/DrawerHeader";
import DrawerContentContainer from "shared-ui/src/components/detail-drawer/DrawerContentContainer";
import { useDrawerStack } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { useOrderDrawerData } from "./useOrderDrawerData";
import { useOrdersStream } from "@features/orders-features/hooks/useOrdersStream";
import { OrderDrawerPayment } from "./sections/OrderDrawerPayment";
import { OrderDrawerAdditionalDetails } from "./sections/OrderDrawerAdditionalDetails";
import { OrderDrawerCheckoutQuestions } from "./sections/OrderDrawerCheckoutQuestions";
import { OrderDrawerApprovalHistory } from "./sections/OrderDrawerApprovalHistory";
import type { OrderDrawerIdentifier, OrderDrawerPreloadedData } from "./types";
import { OrderDrawerHeader } from "@features/orders-features/components/order-drawer/sections/OrderDrawerHeader";
import useGetLegacyOrgUnits from "@features/storefronts-feature/hooks/useGetLegacyOrgUnits";
import { OrderDrawerHeaderActions } from "@features/orders-features/components/order-drawer/sections/OrderDrawerHeaderActions";
import { OrderDrawerOrderProgress } from "@features/orders-features/components/order-drawer/sections/OrderDrawerOrderProgress";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  joinPath,
  useRoutedOverlayBase,
  useRoutedTab,
} from "shared-ui/src/hooks/routedOverlays";
import { OrderLineItemDto } from "@lib/ShiOneClient";
import {
  OrderDrawerBillTo,
  OrderDrawerDetails,
  OrderDrawerInvoices,
  OrderDrawerShippingDetails,
} from "@features/orders-features/components/order-drawer/sections";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import { useGetOrderInvoices } from "@features/orders-features/hooks/useGetOrderInvoices";
import {
  useEmailOrderInvoicesRouteKey,
  useRoutedEmailOrderInvoicesDialog,
  type EmailOrderInvoicesTarget,
} from "@features/orders-features/components/RoutedEmailOrderInvoicesDialog";
import { toOrderRouteKey } from "./orderRouteKey";
import {
  ORDER_DRAWER_DEFAULT_TAB,
  ORDER_DRAWER_TABS,
} from "./orderDrawerSegments";

const ORDER_NOT_FOUND_REASON =
  "This can happen when the order was placed by another user on your account.";

/** Explains a missing order; a cold `bi-`/`web-` link has no sales order to name. */
export function getOrderNotFoundMessage(salesOrder?: string): string {
  return salesOrder
    ? `Order details aren't available for sales order ${salesOrder}. ${ORDER_NOT_FOUND_REASON}`
    : `Order details aren't available. ${ORDER_NOT_FOUND_REASON}`;
}

// The return request dialog sits at modal + 120; the raised drawer must stack just above it.
const RAISED_ORDER_DRAWER_Z_INDEX_OFFSET = 121;

interface OrderDrawerProps {
  open: boolean;
  onClose: () => void;
  identifier: OrderDrawerIdentifier;
  preloadedData: OrderDrawerPreloadedData | null;
  /** The drawer lives in the URL, so closing it also closes the stacked drawers above it. */
  routed?: boolean;
  /** Called once when the order does not resolve; the owner tells the user and closes the drawer. */
  onNotFound?: (salesOrder?: string) => void;
  /** Called when the close animation finishes, so the owner can unmount the drawer. */
  onExited?: () => void;
  /** Stacks the drawer above the return request dialog, which is hosted over the page. */
  raised?: boolean;
}

export function OrderDrawer({
  open,
  onClose,
  identifier,
  preloadedData,
  routed = false,
  onNotFound,
  onExited,
  raised = false,
}: Readonly<OrderDrawerProps>) {
  const {
    isLoading: isLoadingOrderDrawerData,
    isError,
    hasData,
    orderData,
  } = useOrderDrawerData({
    identifier,
    preloadedData,
    enabled: open,
  });

  const { data: storefronts, isLoading: isLoadingOrgUnits } =
    useGetLegacyOrgUnits();

  const { syncStatus, lastSyncedAt } = useOrdersStream();

  const { closeAll } = useDrawerStack();

  const buyableItems = useMemo(() => {
    const orderLineProducts: Record<string, OrderLineItemDto[]> =
      orderData?.orderLineProducts ?? {};
    return Object.values(orderLineProducts)
      .flat()
      .filter((i) => i.productDescriptor || i.mfgPartNumber || i.shiPartNumber);
  }, [orderData?.orderLineProducts]);

  const resolvedIdentifiers = useMemo(
    () => ({
      ...identifier,
      // A cold `bi-`/`web-` link only learns the sales order from the fetched order.
      salesOrder: orderData?.salesOrder || identifier.salesOrder,
      biOrderId: orderData?.biOrderId ?? identifier.biOrderId,
      webOrderId: orderData?.webOrderId ?? identifier.webOrderId,
      orgUnitId: orderData?.orgUnitId ?? identifier.orgUnitId,
    }),
    [
      identifier,
      orderData?.salesOrder,
      orderData?.biOrderId,
      orderData?.webOrderId,
      orderData?.orgUnitId,
    ],
  );

  // Same fallback as the OrderDrawerHeader title, so a cold `bi-`/`web-` link is named before it loads.
  const orderLabel =
    resolvedIdentifiers.salesOrder ||
    resolvedIdentifiers.biOrderId ||
    resolvedIdentifiers.webOrderId;

  const isLoading = isLoadingOrderDrawerData || isLoadingOrgUnits;

  const isNotFound = !isLoading && !isError && !hasData;
  const hasReportedNotFoundRef = useRef(false);
  useEffect(() => {
    // Each open reports at most once; reopening the same order may report again.
    if (!open) {
      hasReportedNotFoundRef.current = false;
      return;
    }
    if (!isNotFound || !onNotFound || hasReportedNotFoundRef.current) {
      return;
    }
    hasReportedNotFoundRef.current = true;
    onNotFound(resolvedIdentifiers.salesOrder || undefined);
  }, [open, isNotFound, onNotFound, resolvedIdentifiers.salesOrder]);

  // The invoices section offers Email Invoice only with this permission and loaded invoices;
  // a refreshed dialog route checks the same before it reopens. Shares the section's query.
  const { hasPermission, claimsLoading } = useClaims();
  const offersInvoiceEmail =
    hasPermission(ShiOnePermission.orderSearchOrgUnit) &&
    !!orderData?.hasInvoice;
  const hasInvoiceQueryIdentifiers =
    !!resolvedIdentifiers.biOrderId &&
    !!resolvedIdentifiers.salesOrder &&
    !!orderData?.purchaseOrder;
  const invoicesQuery = useGetOrderInvoices({
    biOrderId: offersInvoiceEmail
      ? (resolvedIdentifiers.biOrderId ?? undefined)
      : undefined,
    orderNumber: resolvedIdentifiers.salesOrder,
    custPONumber: orderData?.purchaseOrder,
  });
  const orderBase = useRoutedOverlayBase();
  const orderTabs = useRoutedTab({
    tabs: ORDER_DRAWER_TABS,
    defaultTab: ORDER_DRAWER_DEFAULT_TAB,
    replaceHistory: false,
    // Legacy order URLs can continue directly to a child drawer or dialog.
    ready: false,
  });
  // Legacy tab-less routes place child dialogs directly under the order base.
  const dialogBase =
    routed && orderBase !== undefined
      ? orderTabs.isTabInPath
        ? joinPath(orderBase, orderTabs.activeTab)
        : orderBase
      : null;
  const ownOrderKey = toOrderRouteKey(identifier);
  const emailOrderKey = useEmailOrderInvoicesRouteKey(dialogBase);
  const emailInvoicesTarget = useMemo<EmailOrderInvoicesTarget | null>(
    () =>
      offersInvoiceEmail &&
      hasInvoiceQueryIdentifiers &&
      (invoicesQuery.data?.length ?? 0) > 0 &&
      (emailOrderKey === undefined || emailOrderKey === ownOrderKey)
        ? {
            salesOrder: resolvedIdentifiers.salesOrder,
            biOrderId: resolvedIdentifiers.biOrderId ?? undefined,
            custPONumber: orderData?.purchaseOrder,
          }
        : null,
    [
      offersInvoiceEmail,
      hasInvoiceQueryIdentifiers,
      invoicesQuery.data,
      emailOrderKey,
      ownOrderKey,
      resolvedIdentifiers.salesOrder,
      resolvedIdentifiers.biOrderId,
      orderData?.purchaseOrder,
    ],
  );
  const { open: openEmailInvoices, dialog: emailInvoicesDialog } =
    useRoutedEmailOrderInvoicesDialog({
      basePath: dialogBase,
      target: emailInvoicesTarget,
      isTargetSettled:
        !claimsLoading &&
        !isLoading &&
        (!offersInvoiceEmail ||
          !hasInvoiceQueryIdentifiers ||
          invoicesQuery.isSuccess ||
          invoicesQuery.isError),
    });
  const handleEmailInvoices = useCallback(
    () => openEmailInvoices(ownOrderKey),
    [openEmailInvoices, ownOrderKey],
  );

  /** Handles close from the Order Drawer itself - also clears any stacked drawers */
  const handleOrderDrawerClose = useCallback(() => {
    // A routed close returns to the page path, which removes the stacked levels too; a
    // second close would step back from the same entry and go back too far.
    if (!routed) {
      closeAll();
    }
    onClose();
  }, [routed, closeAll, onClose]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleOrderDrawerClose}
        sx={
          raised ? { zIndex: (theme) => theme.zIndex.modal + RAISED_ORDER_DRAWER_Z_INDEX_OFFSET } : undefined
        }
        role="dialog"
        aria-label={
          orderLabel ? `Order details - ${orderLabel}` : "Order details"
        }
        slotProps={{
          transition: { onExited },
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
            },
          },
        }}
      >
        <DrawerContentContainer
          sx={(theme) => ({
            [theme.breakpoints.up("lg")]: { width: "45vw" },
            [theme.breakpoints.down("lg")]: { width: "60vw" },
          })}
        >
          <DrawerHeader
            iconElement={
              <OrderDrawerHeader
                order={orderData}
                orgUnits={storefronts?.orgUnits ?? []}
              />
            }
            onClose={handleOrderDrawerClose}
            customRightHeaderElement={
              <OrderDrawerHeaderActions
                id={resolvedIdentifiers}
                order={orderData}
                canReturn={orderData?.canReturn}
                buyableItems={buyableItems}
              />
            }
          />
          <Divider />

          <Box sx={{ flex: 1, overflow: "auto", bgcolor: "table.header" }}>
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 200,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {isError && !hasData && (
              <Box sx={{ p: 3 }}>
                <Typography color="error" variant="body2">
                  Unable to load order details. Please try again later.
                </Typography>
              </Box>
            )}

            {isNotFound && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary" variant="body2">
                  {getOrderNotFoundMessage(resolvedIdentifiers.salesOrder)}
                </Typography>
              </Box>
            )}

            {hasData && orderData && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 3 }}
              >
                <OrderDrawerOrderProgress
                  order={orderData}
                  syncStatus={syncStatus}
                  lastSyncedAt={lastSyncedAt}
                />
                <OrderDrawerDetails
                  order={orderData}
                  tabControls={
                    routed && orderBase !== undefined ? orderTabs : undefined
                  }
                />
                <OrderDrawerShippingDetails
                  id={resolvedIdentifiers}
                  shipTo={orderData?.shipTo}
                />
                <OrderDrawerPayment
                  identifier={resolvedIdentifiers}
                  enabled={open}
                />
                {orderData.hasInvoice && (
                  <OrderDrawerInvoices
                    {...resolvedIdentifiers}
                    custPONumber={orderData.purchaseOrder}
                    currencyCode={orderData.currencyCode}
                    onEmailInvoices={handleEmailInvoices}
                  />
                )}
                <OrderDrawerBillTo {...resolvedIdentifiers} />
                <OrderDrawerCheckoutQuestions
                  identifier={{
                    ...identifier,
                    biOrderId: orderData.biOrderId ?? identifier.biOrderId,
                    webOrderId: orderData.webOrderId ?? identifier.webOrderId,
                  }}
                  enabled={open}
                />
                <OrderDrawerAdditionalDetails
                  identifier={{
                    ...identifier,
                    biOrderId: orderData.biOrderId ?? identifier.biOrderId,
                    webOrderId: orderData.webOrderId ?? identifier.webOrderId,
                  }}
                  enabled={open}
                />
                <OrderDrawerApprovalHistory
                  identifier={{
                    ...identifier,
                    biOrderId: orderData.biOrderId ?? identifier.biOrderId,
                    webOrderId: orderData.webOrderId ?? identifier.webOrderId,
                  }}
                  enabled={open}
                  order={orderData}
                />
              </Box>
            )}
          </Box>
        </DrawerContentContainer>
      </Drawer>
      {emailInvoicesDialog}
    </>
  );
}
